import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RotateCcw } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "./ui/alert-dialog";
import { wabaDate } from "./WabaDiagnostics";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STATUS = { queued: "В очереди", sending: "Запрос выполняется", accepted: "Принято Meta",
  sent: "Отправлено · webhook", delivered: "Доставлено", read: "Прочитано", failed: "Ошибка", skipped: "Исключён" };

export const WabaRecipientList = ({ campaign, group, onRetried }) => {
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [jump, setJump] = useState("");
  const scopeRef = useRef("");
  // Eligibility must unlock when the 24h boundary passes even without new webhooks.
  useEffect(() => {
    const timer = setInterval(() => setTick(v => v + 1), 30000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => { setOffset(0); setStatus(""); }, [campaign.id, group]);
  useEffect(() => {
    const controller = new AbortController();
    const scope = `${campaign.id}:${group}:${status}:${offset}:${pageSize}`;
    if (scopeRef.current !== scope) setLoading(true);
    scopeRef.current = scope;
    setError("");
    axios.get(`${API}/admin/waba/campaigns/${campaign.id}/recipients`, {
      params: { status: group !== "" ? "failed" : status, error_code: group, offset, limit: pageSize },
      signal: controller.signal, withCredentials: true,
    }).then(r => { setResult(r.data); setLoading(false); }).catch(e => {
      if (e.code !== "ERR_CANCELED") { setError(e.response?.data?.detail || "Не удалось получить телефоны"); setLoading(false); }
    });
    return () => controller.abort();
  }, [campaign.id, campaign.last_webhook_at, campaign.failed, campaign.queued, group, status, offset, pageSize, tick]);
  const pages = Math.max(1, Math.ceil((result.total || 0) / pageSize));
  const page = Math.floor(offset / pageSize) + 1;
  const goPage = (n) => {
    const target = Math.min(Math.max(1, n), pages);
    setOffset((target - 1) * pageSize);
  };
  const doRetry = async () => {
    if (!retry) return;
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/waba/campaigns/${campaign.id}/retry`,
        { recipient_ids: [retry.id], confirmed: true }, { withCredentials: true });
      setRetry(null); onRetried(r.data.id);
    } catch (e) { setError(e.response?.data?.detail || "Повтор не выполнен"); }
    finally { setBusy(false); }
  };
  return <div className="waba-recipient-list" data-testid="waba-recipient-list">
    <div className="waba-toolbar">
      <h3 data-testid="waba-recipient-count">Получатели · {result.total}</h3>
      <select data-testid="waba-recipient-status-filter" aria-label="Статус получателей" className="waba-tool" value={group !== "" ? "failed" : status}
        disabled={group !== ""} onChange={e => { setStatus(e.target.value); setOffset(0); }}>
        <option value="">Все статусы</option>
        {Object.entries(STATUS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </div>
    {error && <p role="alert" className="waba-danger" data-testid="waba-recipients-error">{error}</p>}
    {loading && <p className="waba-muted" data-testid="waba-recipients-loading">Обновление…</p>}
    {!loading && !result.items.length && <p className="waba-muted" data-testid="waba-recipients-empty">Получателей с таким статусом нет</p>}
    {!loading && result.items.map(r => <div key={r.id} className="waba-recipient" data-testid={`waba-recipient-${r.id}`}>
      <details data-testid={`waba-recipient-details-${r.id}`}>
        <summary data-testid={`waba-recipient-open-${r.id}`}>
          <strong data-testid={`waba-recipient-phone-${r.id}`}>+{r.phone}</strong>
          <span className={r.status === "failed" ? "waba-danger" : "waba-muted"} data-testid={`waba-recipient-status-${r.id}`}>{STATUS[r.status] || r.status}</span>
          {r.error && <span className="waba-danger waba-recipient-error" data-testid={`waba-recipient-error-${r.id}`}>{r.error}</span>}
        </summary>
        <dl className="waba-receipt" data-testid={`waba-recipient-evidence-${r.id}`}>
          <dt>wamid</dt><dd data-testid={`waba-recipient-wamid-${r.id}`}>{r.wamid || "Не получен"}</dd>
          {[['Принято Meta', 'accepted_at'], ['Отправлено (подтверждено)', 'sent_confirmed_at'], ['Доставлено', 'delivered_at'], ['Прочитано', 'read_at']].map(([label, key]) =>
            <React.Fragment key={key}><dt>{label}</dt><dd data-testid={`waba-recipient-${key.replaceAll('_', '-')}-${r.id}`}>{wabaDate(r[key])}</dd></React.Fragment>)}
          {r.error && <>
            <dt>error.code</dt><dd data-testid={`waba-recipient-code-${r.id}`}>{r.error_code ?? "Код не сохранён"}</dd>
            <dt>error.title</dt><dd data-testid={`waba-recipient-title-${r.id}`}>{r.meta_error?.title || "—"}</dd>
            <dt>error.message</dt><dd data-testid={`waba-recipient-message-${r.id}`}>{r.meta_error?.message || "—"}</dd>
            <dt>error_data.details</dt><dd data-testid={`waba-recipient-meta-details-${r.id}`}>{r.meta_error?.error_data?.details || "—"}</dd>
          </>}
        </dl>
        {r.error_source === "legacy_text" && <p className="waba-warning" data-testid={`waba-recipient-legacy-${r.id}`}>Старая запись: сохранён только текст ошибки. Категория восстановлена из текста, исходный ответ Meta отсутствует.</p>}
        {r.status_conflict && <p className="waba-warning" data-testid={`waba-recipient-conflict-${r.id}`}>Meta прислала и подтверждение доставки, и ошибку. Оба события сохранены; требуется проверка.</p>}
      </details>
      {r.error_code === 131049 && r.status === "failed" && <div className="waba-toolbar">
        <span className="waba-muted" data-testid={`waba-retry-after-${r.id}`}>{r.retry_campaign_id ? "Ручной повтор уже создан" : `Повтор не раньше: ${wabaDate(r.retry_after)}`}</span>
        <button className="waba-tool" data-testid={`waba-retry-btn-${r.id}`} disabled={!r.can_retry || busy} onClick={() => setRetry(r)}>
          <RotateCcw size={13} /> Ручной повтор
        </button>
      </div>}
      {r.error_code === 131026 && <p className="waba-muted" data-testid={`waba-undeliverable-${r.id}`}>Недоставляемый контакт — исключён из дальнейших рассылок.</p>}
    </div>)}
    <div className="waba-pagination">
      <button className="waba-tool" data-testid="waba-recipients-first" aria-label="В начало списка"
        disabled={page <= 1 || loading} onClick={() => goPage(1)}><ChevronsLeft size={16} /></button>
      <button className="waba-tool" data-testid="waba-recipients-prev" aria-label="Предыдущие получатели" disabled={!offset || loading} onClick={() => goPage(page - 1)}><ChevronLeft size={16} /></button>
      <span data-testid="waba-recipients-page">{result.total ? `${offset + 1}–${Math.min(offset + pageSize, result.total)} из ${result.total}` : "0"}</span>
      <button className="waba-tool" data-testid="waba-recipients-next" aria-label="Следующие получатели" disabled={offset + pageSize >= result.total || loading} onClick={() => goPage(page + 1)}><ChevronRight size={16} /></button>
      <button className="waba-tool" data-testid="waba-recipients-last" aria-label="В конец списка"
        disabled={page >= pages || loading} onClick={() => goPage(pages)}><ChevronsRight size={16} /></button>
      <span className="waba-muted" data-testid="waba-recipients-pages">стр. {page} из {pages}</span>
      <input data-testid="waba-recipients-jump" aria-label="Перейти на страницу" className="waba-tool"
        style={{ width: "70px" }} placeholder="стр." value={jump}
        onChange={e => setJump(e.target.value.replace(/\D/g, ""))}
        onKeyDown={e => { if (e.key === "Enter" && jump) { goPage(Number(jump)); setJump(""); } }} />
      <button className="waba-tool" data-testid="waba-recipients-jump-go" disabled={!jump || loading}
        onClick={() => { goPage(Number(jump)); setJump(""); }}>Перейти</button>
      <select data-testid="waba-recipients-page-size" aria-label="Контактов на странице" className="waba-tool"
        value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setOffset(0); }}>
        {[50, 100, 200].map(n => <option key={n} value={n}>по {n}</option>)}
      </select>
    </div>
    <AlertDialog open={!!retry} onOpenChange={v => { if (!v && !busy) setRetry(null); }}>
      <AlertDialogContent data-testid="waba-retry-dialog" className="waba-retry-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="waba-retry-title">Повторить сообщение вручную?</AlertDialogTitle>
          <AlertDialogDescription data-testid="waba-retry-description">Получатель: +{retry?.phone}. Отправитель: {campaign.sender_label}. Будет создана отдельная рассылка. Прошедшие 24 часа не гарантируют доставку; исходная ошибка сохранится.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="waba-retry-cancel" disabled={busy}>Отмена</AlertDialogCancel>
          <AlertDialogAction data-testid="waba-retry-confirm" disabled={busy} onClick={e => { e.preventDefault(); doRetry(); }}>{busy ? "Создаётся…" : "Подтвердить отправку"}</AlertDialogAction>
        </AlertDialogFooter>
        {error && <p role="alert" className="waba-danger" data-testid="waba-retry-dialog-error">{error}</p>}
      </AlertDialogContent>
    </AlertDialog>
  </div>;
};